import React, { useEffect, useMemo } from 'react';
import { Dispatch, RootState } from '../../../../store/store';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  changeControllerSettings,
  settingsButtonActions,
  settingsFactoryResetButtonActions,
  settingsResetButtonActions,
  settingsResetFaultButtonActions,
} from './settingsActions';
import { Form, Formik } from 'formik';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { CONTROLLER_ACTIONS_BY_ROLE } from '../../../../constants/controller-allowed-actions';
import PidControls from './PidControls';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const Settings: React.FC<ComponentProps> = ({ user, settings, getSettings, reset }) => {
  const { id } = useParams();

  const allowedActionsForUser = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CONTROLLER_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  useEffect(() => {
    getSettings(id);
    const intervalId = setInterval(() => getSettings(id), EXTENDED_INTERVAL_UPDATE_DATA);
    return () => clearInterval(intervalId);
  }, [id]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  if (!settings) {
    return <div className="flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="p-edit-controller-settings pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">Edit Controller Settings</h3>
        </div>
        <div className="flex w-full flex-col justify-start p-7">
          <h2 className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
            Shunt Trip
          </h2>
          <div className="flex w-full flex-row justify-start gap-5.5">
            {settingsButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return 'Not Allowed';
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeControllerSettings(id, item.actionName, item.value)}
                >
                  {(props) => (
                    <Form>
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div>
        <div className="flex w-full flex-col justify-start p-7">
          <h2 className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
            All Channel Settings
          </h2>
          <div className="flex w-full flex-row justify-start gap-5.5">
            {settingsResetButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return 'Not Allowed';
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeControllerSettings(id, item.actionName, '')}
                >
                  {(props) => (
                    <Form>
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div>
        <PidControls controllerId={id} settings={settings} allowedActionsForUser={allowedActionsForUser} />

        <div className="flex w-full flex-col justify-start p-7">
          <h2 className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
            Fault Reset
          </h2>
          <div className="flex w-full flex-row justify-start gap-5.5">
            {settingsResetFaultButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return 'Not Allowed';
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeControllerSettings(id, item.actionName, '')}
                >
                  {(props) => (
                    <Form>
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div>
        <div className="flex w-full flex-col justify-start p-7">
          <h2 className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
            Factory Reset
          </h2>
          <div className="flex w-full flex-row justify-start gap-5.5">
            {settingsFactoryResetButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return 'Not Allowed';
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeControllerSettings(id, item.actionName, '')}
                >
                  {(props) => (
                    <Form>
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: state.user.profile,
  settings: state.controllers.controller_settings,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getSettings: dispatch.controllers.getControllerSettings,
  reset: dispatch.controllers.resetSettings,
});

export default connect(mapState, mapDispatch)(Settings);
